import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";

const GRADE_OPTIONS = ["Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12"];

const emptyForm = {
  chapterId: "",
  title: "",
  grade: "Grade 9",
  description: "",
  order: 0,
  active: true
};

function AdminChapterEditor({ apiBase, token }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { chapterId } = useParams();
  const isCreate = !chapterId;
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState({ type: "", text: "" });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const chapter = location.state?.chapter;
    if (isCreate || !chapter || chapter.chapterId !== chapterId) return;
    setForm({
      chapterId: chapter.chapterId || "",
      title: chapter.title || "",
      grade: chapter.grade || "Grade 9",
      description: chapter.description || "",
      order: Number(chapter.order) || 0,
      active: chapter.active !== false
    });
  }, [location.state, isCreate, chapterId]);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (isCreate) return;
      setLoading(true);
      try {
        const response = await fetch(`${apiBase}/api/admin/chapters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) throw new Error("Failed to load chapter");
        const payload = await response.json();
        const found = (payload.chapters || []).find((item) => item.chapterId === chapterId);
        if (!found) throw new Error("Chapter not found");
        if (mounted) {
          setForm({
            chapterId: found.chapterId || "",
            title: found.title || "",
            grade: found.grade || "Grade 9",
            description: found.description || "",
            order: Number(found.order) || 0,
            active: found.active !== false
          });
        }
      } catch (err) {
        if (mounted) setNotice({ type: "error", text: err.message || "Failed to load chapter" });
      } finally {
        if (mounted) setLoading(false);
      }
    }
    if (token) load();
    return () => {
      mounted = false;
    };
  }, [apiBase, token, chapterId, isCreate]);

  async function saveChapter() {
    if (!form.chapterId.trim() || !form.title.trim()) {
      setNotice({ type: "error", text: "Chapter ID and title are required." });
      return;
    }
    setSaving(true);
    setNotice({ type: "", text: "" });
    try {
      const response = await fetch(`${apiBase}/api/admin/chapters`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to save chapter");
      }
      setNotice({ type: "success", text: "Chapter saved." });
      if (isCreate) {
        navigate(`/admin/chapters/${form.chapterId}`);
      }
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to save chapter" });
    } finally {
      setSaving(false);
    }
  }

  async function setActive(nextActive) {
    if (!form.chapterId) return;
    setSaving(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/chapters/${form.chapterId}/active`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ active: nextActive })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update status");
      }
      setForm((prev) => ({ ...prev, active: nextActive }));
      setNotice({ type: "success", text: `Chapter set ${nextActive ? "active" : "inactive"}.` });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to update status" });
    } finally {
      setSaving(false);
    }
  }

  async function removeChapter() {
    if (!form.chapterId) return;
    setSaving(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/chapters/${form.chapterId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to delete chapter");
      }
      navigate("/admin/chapters");
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Failed to delete chapter" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="admin-panel">
      <div className="admin-page-header">
        <Link className="secondary-button" to="/admin/chapters">
          Back
        </Link>
        <div className="analysis-label">{isCreate ? "Create Chapter" : "Update Chapter"}</div>
      </div>
      {notice.text && (
        <div className={`admin-notice ${notice.type === "error" ? "error" : "success"}`}>{notice.text}</div>
      )}
      <div className="admin-editor">
        {loading && <div className="analysis-note">Loading chapter...</div>}
        <label>Chapter ID</label>
        <input
          value={form.chapterId}
          onChange={(event) => setForm((prev) => ({ ...prev, chapterId: event.target.value }))}
          disabled={!isCreate}
        />
        <label>Chapter Name</label>
        <input value={form.title} onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))} />
        <label>Grade</label>
        <select value={form.grade} onChange={(event) => setForm((prev) => ({ ...prev, grade: event.target.value }))}>
          {GRADE_OPTIONS.map((grade) => (
            <option key={grade} value={grade}>
              {grade}
            </option>
          ))}
        </select>
        <label>Description</label>
        <textarea
          rows={4}
          value={form.description}
          onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
        />
        <label>Order</label>
        <input
          type="number"
          value={form.order}
          onChange={(event) => setForm((prev) => ({ ...prev, order: Number(event.target.value) || 0 }))}
        />
        <div className="admin-actions">
          <button className="primary-button" type="button" onClick={saveChapter} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
          {!isCreate && (
            <>
              <button className="secondary-button" type="button" onClick={() => setActive(!form.active)} disabled={saving}>
                {form.active ? "Make Inactive" : "Make Active"}
              </button>
              <button className="danger-button" type="button" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
                Delete
              </button>
            </>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete chapter?"
        message="This will permanently delete the chapter from the admin catalog."
        confirmLabel="Delete Chapter"
        busy={saving}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setShowDeleteConfirm(false);
          await removeChapter();
        }}
      />
    </div>
  );
}

export default AdminChapterEditor;
